<template>
  <div>
    <b-sidebar
      id="add-new-patient-sidebar"
      :visible="isAddNewPatientSidebarActive"
      bg-variant="white"
      sidebar-class="sidebar-lg"
      shadow
      backdrop
      no-header
      right
      @hidden="resetForm"
      @change="val => $emit('update:is-add-new-patient-sidebar-active', val)">
      <template #default="{ hide }">
        <!-- Header -->
        <div
          class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">{{ $t('Add') }} {{ $t('Patient') }}</h5>
          <i class="fas fa-times ml-1 cursor-pointer" size="16" @click="hide"></i>
        </div>

        <!-- BODY -->
        <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
          <!-- Form -->
          <b-form
            class="p-2"
            @submit.prevent="handleSubmit(onSubmit)"
            @reset.prevent="resetForm">
            <!-- First Name -->
            <validation-provider
              #default="validationContext"
              name="First Name"
              rules="required">
              <b-form-group label-for="name">
                <label for="name">{{ $t('Form.Name') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <b-form-input
                  id="name"
                  v-model="stateData.name"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="First Name"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Last Name -->
            <validation-provider
              #default="validationContext"
              name="Last Name"
              rules="required">
              <b-form-group label-for="lastname">
                <label for="lastname">{{ $t('Form.Last Name') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <b-form-input
                  id="lastname"
                  v-model="stateData.lastname"
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Last Name"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Phone -->
            <validation-provider
              #default="validationContext"
              name="Phone"
              rules="required|max:14">
              <b-form-group label-for="contact">
                <label for="contact">{{ $t('Form.Phone') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <b-form-input
                  id="contact"
                  v-model="formatPhone"
                  :state="getValidationState(validationContext)"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Gender -->
            <validation-provider #default="validationContext" name="Gender" rules="required">
              <b-form-group label-for="gender">
                <label for="gender">{{ $t('Form.Gender') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <treeselect
                  id="gender"
                  v-model="stateData.gender"
                  :options="genderOptions"
                  :normalizer="normalizer"
                  :class="{
                    'custom-treeselect': true,
                    'is-invalid': getValidationState(validationContext) === false
                  }"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Type visit -->
            <validation-provider
              #default="validationContext"
              name="Type visit"
              rules="required">
              <b-form-group label-for="type_visit">
                <label for="type_visit">{{ $t('Type visit') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <treeselect
                  id="type_visit"
                  v-model="stateData.type_visits_id"
                  :options="optionsTypeVisit"
                  :normalizer="normalizer"
                  :class="{
                    'custom-treeselect': true,
                    'is-invalid': getValidationState(validationContext) === false
                  }"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Insurance Agent -->
            <validation-provider
              #default="validationContext"
              name="Insurance Agent"
              rules="required">
              <b-form-group label-for="agent_insurance">
                <label for="agent_insurance">
                  {{ $t('Insurance Agent') }}
                  <span class="text-danger">*</span>
                  <small class="text-muted">({{ $t('Required') }})</small>
                </label>
                <treeselect
                  id="agent_insurance"
                  v-model="stateData.agent_insurance_id"
                  :options="optionsAgentInsurance"
                  :normalizer="normalizer"
                  placeholder="Select agent"
                  :class="{
                    'custom-treeselect': true,
                    'is-invalid': getValidationState(validationContext) === false
                  }"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Name insurance -->
            <validation-provider
              #default="validationContext"
              name="Name Insurance"
              rules="required">
              <b-form-group label-for="name_insurance">
                <label for="name_insurance">{{ $t('Name insurance') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <treeselect
                  id="name_insurance"
                  v-model="stateData.nameInsuranceId"
                  :options="optionsNameInsurance"
                  :normalizer="normalizer"
                  :class="{
                    'custom-treeselect': true,
                    'is-invalid': getValidationState(validationContext) === false
                  }"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Member ID -->
            <validation-provider
              #default="validationContext"
              name="Member ID"
              rules="required|max:255">
              <b-form-group label-for="member_id">
                <label for="member_id">
                  Member ID
                  <span class="text-danger">*</span>
                  <small class="text-muted">({{ $t('Required') }})</small>
                </label>
                <b-form-input
                  id="member_id"
                  v-model="stateData.member_id"
                  :state="getValidationState(validationContext)"
                  trim
                  :maxlength="memberIdMaxLength"
                  placeholder="Enter member identifier"
                  @blur="validationContext.validate" />
                <div class="d-flex justify-content-between mt-1">
                  <small class="text-muted">
                    {{ memberIdLength }} / {{ memberIdMaxLength }}
                  </small>
                  <small class="text-muted">
                    {{ memberIdRemaining }}
                    {{ memberIdRemaining === 1 ? 'character left' : 'characters left' }}
                  </small>
                </div>
                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Conditional Content -->
            <div v-if="showConditionalContent">
              <membership @dataMemberchip="handleDataMemberchip" />
            </div>

            <!-- Visit Reason -->
            <validation-provider
              #default="validationContext"
              name="Visit Reason"
              rules="required">
              <b-form-group label-for="visit_reason">
                <label for="visit_reason">{{ $t('Visit Reason') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <treeselect
                  id="visit_reason"
                  v-model="stateData.reason_visit"
                  :options="visitReasonOptions"
                  :normalizer="normalizer"
                  :searchable="true"
                  :clearable="true"
                  :multiple="false"
                  placeholder="Select a visit reason"
                  :max-height="200"
                  :options-limit="50"
                  :class="{
                    'custom-treeselect': true,
                    'is-invalid': getValidationState(validationContext) === false
                  }"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Other Reason Input (conditional) -->
            <validation-provider
              v-if="showOtherReasonInput"
              #default="validationContext"
              name="Other Reason"
              rules="required">
              <b-form-group label-for="reason_visit_other">
                <label for="reason_visit_other">{{ $t('Please specify') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <b-form-textarea
                  id="reason_visit_other"
                  v-model="stateData.reason_visit_other"
                  :state="getValidationState(validationContext)"
                  placeholder="Please specify the reason for your visit"
                  rows="3"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Reference -->
            <validation-provider
              v-if="showConditionalContentRef"
              #default="validationContext"
              name="Reference"
              rules="required">
              <b-form-group label-for="reference">
                <label for="reference">{{ $t('Reference') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <treeselect
                  id="reference"
                  v-model="stateData.user_references_id"
                  :options="optionsReferenceList"
                  :normalizer="normalizer"
                  :class="{
                    'custom-treeselect': true,
                    'is-invalid': getValidationState(validationContext) === false
                  }"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Address -->
            <validation-provider
              #default="validationContext"
              name="Address"
              rules="required">
              <b-form-group label-for="address">
                <label for="address">Address <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <b-form-input
                  id="address"
                  v-model="stateData.address"
                  :state="getValidationState(validationContext)"
                  placeholder="Enter address"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- City: input + lista automática debajo (debe seleccionar de la lista) -->
            <validation-provider
              #default="validationContext"
              name="City"
              rules="required">
              <b-form-group
                label-for="city"
                class="city-autocomplete-wrap">
                <template #label>
                  {{ $t('Form.City') }} <span class="text-danger">*</span>
                  <small class="text-muted">({{ $t('Required') }})</small>
                </template>
                <small class="d-block text-info mb-1">
                  <i class="fas fa-info-circle mr-1"></i>
                  Escriba el nombre de la ciudad y luego <strong>debe hacer clic en una opción de la lista</strong> para seleccionarla.
                </small>
                <b-form-input
                  id="city"
                  v-model="cityName"
                  :state="stateData.cities_id ? true : (cityName ? false : null)"
                  placeholder="Escriba y seleccione de la lista desplegable"
                  @input="searchCitiesFromInput"
                  @focus="cityName && searchCitiesFromInput()"
                  @blur="validationContext.validate" />
                <div v-if="cityOptions.length" class="city-options-list">
                  <div
                    v-for="opt in cityOptions"
                    :key="opt.value"
                    class="city-option-item"
                    @mousedown.prevent="selectCityOption(opt)">
                    <i class="fas fa-mouse-pointer mr-1 text-muted small"></i>{{ opt.text }}
                  </div>
                </div>
                <b-form-invalid-feedback v-if="cityName && !stateData.cities_id">
                  Debe seleccionar una ciudad de la lista haciendo clic en ella.
                </b-form-invalid-feedback>
                <b-form-invalid-feedback v-else>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Zip Code -->
            <validation-provider
              #default="validationContext"
              name="Zip Code"
              rules="required">
              <b-form-group label="Zip Code" label-for="zip-code">
                <b-form-input
                  id="zip-code"
                  v-model="stateData.zip_codes_id"
                  :state="getValidationState(validationContext)"
                  type="number"
                  placeholder="Enter zip code"
                  @blur="validationContext.validate" />
                <b-form-invalid-feedback>{{ validationContext.errors[0] }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <div>
              <validation-provider
                #default="validationContext"
                name="Copay"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="copay">
                  <label for="copay">{{ $t('Copay') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                  <b-form-input
                    id="copay"
                    v-model="stateData.copay"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="Copay"
                    min="0"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>{{
                    validationContext.errors[0]
                  }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <validation-provider
                #default="validationContext"
                name="Co Insurance"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="co_insurance">
                  <label for="co_insurance">{{ $t('CoInsurance') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                  <b-form-input
                    id="co_insurance"
                    v-model="stateData.co_insurance"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="Co Insurance"
                    min="0"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>{{
                    validationContext.errors[0]
                  }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <validation-provider
                #default="validationContext"
                name="Deducible"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="deducible">
                  <label for="deducible">{{ $t('Deducible') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                  <b-form-input
                    id="deducible"
                    v-model="stateData.deducible"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="Deducible"
                    min="0"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>{{
                    validationContext.errors[0]
                  }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>

              <validation-provider
                #default="validationContext"
                name="Out Pocket"
                rules="required|min_value:0|numeric">
                <b-form-group label-for="out_pocket">
                  <label for="out_pocket">{{ $t('OutPocket') }} <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                  <b-form-input
                    id="out_pocket"
                    v-model="stateData.out_pocket"
                    type="number"
                    :state="getValidationState(validationContext)"
                    trim
                    placeholder="Out Pocket"
                    min="0"
                    @blur="validationContext.validate" />
                  <b-form-invalid-feedback>{{
                    validationContext.errors[0]
                  }}</b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </div>

            <!-- Date of Birth -->
            <validation-provider
              #default="validationContext"
              name="Date of Birth"
              rules="required">
              <b-form-group label-for="date-birth">
                <label for="date-birth">DOB <span class="text-danger">*</span> <small class="text-muted">({{ $t('Required') }})</small></label>
                <flat-pickr
                  v-model="stateData.date_birth"
                  :config="config"
                  class="form-control"
                  :state="getValidationState(validationContext)"
                  placeholder="MM-DD-YYYY"
                  :class="{
                    'is-invalid': validationContext.touched && !validationContext.valid,
                  }"
                  @on-change="() => { validationContext.touched = true; validationContext.validate(); }"
                  @on-close="() => { validationContext.touched = true; validationContext.validate(); }" />
                <b-form-invalid-feedback>{{
                  validationContext.errors[0]
                }}</b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Cupon
            <checkCupon :cupon.sync="stateData.cupon" @cancelCupon="cancelCupon" /> -->

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button variant="primary" class="mr-2" type="submit" :disabled="loading" @click="forceValidation">
                <span v-if="!loading">{{ $t('Add') }}</span>
                <span v-else><SpinnerLoading /></span>
              </b-button>
              <b-button type="button" variant="outline-danger" @click="hide">{{
                $t('Cancel')
              }}</b-button>
            </div>
          </b-form>
        </validation-observer>
      </template>
    </b-sidebar>
    <ToastNotification ref="toast" />
  </div>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormTextarea,
} from 'bootstrap-vue'
import formValidation from '@/core/utils/forms/form-validation'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import SpinnerLoading from '@/components/SpinnerLoading'
import flatPickr from 'vue-flatpickr-component'
import { required } from '@validations'
import { computed, onMounted, ref, watch } from '@vue/composition-api'
import ToastNotification from '@/components/ToastNotification'
import checkCupon from '@/components/checkCupon'
import membership from './MemberchipAdd.vue'
import { notificationCheck } from '@/mixins/NotificationSetup'
import axiosP from '@/core/services/apiInsurance/patient'
import axiosC from '@/core/services/apiInsurance/admin/cities'
import axiosTV from '@/core/services/apiInsurance/admin/consults/visits'
import axiosNI from '@/core/services/apiInsurance/admin/consults/insuranceName'
import axiosA from '@/core/services/apiInsurance/appointment/actions'

export default {
  components: {
    BFormTextarea,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    ToastNotification,
    BButton,
    SpinnerLoading,
    flatPickr,
    ValidationProvider,
    ValidationObserver,
    // checkCupon,
    membership,
  },
  model: {
    prop: 'isAddNewPatientSidebarActive',
    event: 'update:is-add-new-patient-sidebar-active',
  },
  props: {
    isAddNewPatientSidebarActive: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      required,
      genderOptions: [
        { name: 'Male', id: 'male' },
        { name: 'Female', id: 'female' },
      ],
      visitReasonOptions: [
        { id: 'Other', name: 'Other' },
        { id: 'Abdominal Pain', name: 'Abdominal Pain' },
        { id: 'Allergies', name: 'Allergies' },
        { id: 'Anxiety', name: 'Anxiety' },
        { id: 'Back Pain', name: 'Back Pain' },
        { id: 'Breast Pain', name: 'Breast Pain' },
        { id: 'Chest Pain', name: 'Chest Pain' },
        { id: 'Cold Symptoms', name: 'Cold Symptoms' },
        { id: 'Consultation', name: 'Consultation' },
        { id: 'Cough', name: 'Cough' },
        { id: 'COVID', name: 'COVID' },
        { id: 'Dermatitis', name: 'Dermatitis' },
        { id: 'Diabetes', name: 'Diabetes' },
        { id: 'Diarrhea', name: 'Diarrhea' },
        { id: 'DIU Removal', name: 'DIU Removal' },
        { id: 'Dizziness', name: 'Dizziness' },
        { id: 'DM Control', name: 'DM Control' },
        { id: 'Earache', name: 'Earache' },
        { id: 'Exams', name: 'Exams' },
        { id: 'Fever', name: 'Fever' },
        { id: 'Flu', name: 'Flu' },
        { id: 'Follow Up', name: 'Follow Up' },
        { id: 'General Check', name: 'General Check' },
        { id: 'General Exam', name: 'General Exam' },
        { id: 'Headache', name: 'Headache' },
        { id: 'Health Checkup', name: 'Health Checkup' },
        { id: 'HTN Control', name: 'HTN Control' },
        { id: 'Ingrown Toe Nail', name: 'Ingrown Toe Nail' },
        { id: 'Injection', name: 'Injection' },
        { id: 'IV Therapy', name: 'IV Therapy' },
        { id: 'Knee Pain', name: 'Knee Pain' },
        { id: 'Lab Result', name: 'Lab Result' },
        { id: 'Lower Back Pain', name: 'Lower Back Pain' },
        { id: 'Menstrual Disorder', name: 'Menstrual Disorder' },
        { id: 'Pending Injection', name: 'Pending Injection' },
        { id: 'Physical School', name: 'Physical School' },
        { id: 'Pregnancy Test', name: 'Pregnancy Test' },
        { id: 'Rash', name: 'Rash' },
        { id: 'Refill', name: 'Refill' },
        { id: 'Respiratory Disorder', name: 'Respiratory Disorder' },
        { id: 'Result', name: 'Result' },
        { id: 'Semaglutide', name: 'Semaglutide' },
        { id: 'Shoulder pain', name: 'Shoulder pain' },
        { id: 'Skin Lesion', name: 'Skin Lesion' },
        { id: 'Sore Throat', name: 'Sore Throat' },
        { id: 'Stomachache', name: 'Stomachache' },
        { id: 'USG', name: 'USG' },
        { id: 'Vitamin pack', name: 'Vitamin pack' },
        { id: 'X Ray', name: 'X Ray' },
      ],
      normalizer(node) {
        return {
          id: node.id,
          label: node.name,
        }
      },
    }
  },
  setup(props, { emit }) {
    const stateDataTem = {
      name: '',
      lastname: '',
      phone: '',
      date_birth: '',
      cupon: '',
      type_visits_id: null,
      gender: null,
      codeQr: null,
      user_references_id: null,
      nameInsuranceId: null,
      agent_insurance_id: null,
      address: '',
      cities_id: null,
      zip_codes_id: null,
      copay: '',
      co_insurance: '',
      deducible: '',
      out_pocket: '',
      reason_visit: null,
      reason_visit_other: '',
      member_id: '',
    }

    const toast = ref(null)
    const loading = ref(false)
    const inputScan = ref(null)
    const checkCodeQr = ref(false)
    const optionsTypeVisit = ref([])
    const optionsNameInsurance = ref([])
    const optionsAgentInsurance = ref([])
    const clearTimeoutBuscador = ref('')
    const optionsReferenceList = ref([])
    const cityName = ref('')
    const cityOptions = ref([])
    const setTimeoutCitySearch = ref(null)
    const isAddNewConsultSidebarActive = ref(false)
    const stateData = ref(JSON.parse(JSON.stringify(stateDataTem)))
    const membershipData = ref(null)

    const searchCitiesFromInput = () => {
      if (!cityName.value || !cityName.value.length) {
        cityOptions.value = []
        return
      }
      clearTimeout(setTimeoutCitySearch.value)
      setTimeoutCitySearch.value = setTimeout(() => {
        axiosC
          .citiesFilterFilters({ name: cityName.value })
          .then((response) => {
            const list = Array.isArray(response) ? response : (response && response.data) || []
            cityOptions.value = (list || []).map(city => ({
              text: city.name,
              value: city.id,
            }))
          })
          .catch(() => {
            cityOptions.value = []
          })
      }, 350)
    }

    const onCitySelect = () => {
      const selected = cityOptions.value.find(opt => opt.value === stateData.value.cities_id)
      if (selected) cityName.value = selected.text
    }

    const selectCityOption = (opt) => {
      stateData.value.cities_id = opt.value
      cityName.value = opt.text
      cityOptions.value = []
    }

    const memberIdMaxLength = 255

    const resetuserData = () => {
      stateData.value = JSON.parse(JSON.stringify(stateDataTem))
      cityName.value = ''
      cityOptions.value = []
    }

    const cancelCupon = () => {
      stateData.value.cupon = ''
    }

    const clearOtherReason = () => {
      if (stateData.value.reason_visit !== 'Other') {
        stateData.value.reason_visit_other = ''
      }
    }

    watch(
      () => stateData.value.codeQr,
      newValue => {
        if (newValue !== null || newValue.length > 0) {
          const data = { codeQr: stateData.value.codeQr }
          axiosA.appointmentAssignCuppon(data).then(res => {
            const { type, message } = notificationCheck(res)
            toast.value[type](message)
          })
        }
      }
    )

    watch(
      () => stateData.value.reason_visit,
      () => {
        clearOtherReason()
      }
    )

    // Watcher para validar Date of Birth cuando cambie
    watch(
      () => stateData.value.date_birth,
      () => {
        if (refFormObserver.value && refFormObserver.value.fields) {
          const dateField = refFormObserver.value.fields['Date of Birth']
          if (dateField) {
            dateField.touched = true
            dateField.validate()
          }
        }
      }
    )

    // Watcher para forzar validación cuando cambien los datos
    watch(
      () => stateData.value,
      () => {
        if (refFormObserver.value) {
          setTimeout(() => {
            refFormObserver.value.validate()
          }, 50)
        }
      },
      { deep: true }
    )

    const showConditionalContent = computed(() => {
      return stateData.value.type_visits_id === 8
    })

    const showConditionalContentRef = computed(() => {
      return stateData.value.type_visits_id === 2 || stateData.value.type_visits_id === 3 || stateData.value.type_visits_id === 13 || stateData.value.type_visits_id === 14
    })

    const showOtherReasonInput = computed(() => {
      return stateData.value.reason_visit === 'Other'
    })

    const scanPromotion = () => {
      if (checkCodeQr.value) {
        stateData.value.codeQr = null
      }
      checkCodeQr.value = !checkCodeQr.value
    }

    const formatPhone = computed({
      get() {
        let { phone } = stateData.value
        phone = phone.toString()
        const match = phone
          .replace(/\D+/g, '')
          .replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
        return match
      },
      set(val) {
        stateData.value.phone = val
      },
    })

    const createConsult = () => {
      loadTypeVisit()
      isAddNewConsultSidebarActive.value = false
    }

    /**
     * Filtra los tipos de visita para mostrar solo los relacionados con insurance
     * Incluye tipos que contengan "insurance" en el nombre y "all"/"todos" si existen
     */
    const filterInsuranceVisitTypes = visitTypes => {
      if (!Array.isArray(visitTypes)) return []
      return visitTypes.filter(visit => {
        const name = (visit.name || '').toString().toLowerCase()
        // Incluir tipos que contengan "insurance" o sean "all"/"todos"
        return name.includes('insurance') || name === 'all' || name === 'todos'
      })
    }

    const searchTypeVisit = (name, loading) => {
      if (name.length) {
        const datos = { name: name, status: true }
        loading(true)
        clearTimeout(clearTimeoutBuscador.value)
        clearTimeoutBuscador.value = setTimeout(() => {
          axiosTV.visitFilter(20, datos).then(({ data }) => {
            loading(false)
            // Filtrar solo tipos de visita relacionados con insurance
            optionsTypeVisit.value = filterInsuranceVisitTypes(data)
          })
        }, 400)
      }
    }

    const loadTypeVisit = () => {
      axiosTV.visitList(20).then(({ data }) => {
        // Filtrar solo tipos de visita relacionados con insurance
        optionsTypeVisit.value = filterInsuranceVisitTypes(data)
      })
    }

    const loadNameInsurance = () => {
      axiosNI.visitList(20).then(({ data }) => {
        optionsNameInsurance.value = data
      })
    }

    const loadInsuranceAgents = () => {
      axiosP
        .getInsuranceAgents()
        .then(response => {
          let rawAgents = []

          if (Array.isArray(response?.data?.data)) {
            rawAgents = response.data.data
          } else if (Array.isArray(response?.data)) {
            rawAgents = response.data
          } else if (Array.isArray(response)) {
            rawAgents = response
          }

          optionsAgentInsurance.value = rawAgents.map(agent => {
            const fullName = [agent?.name, agent?.lastname].filter(Boolean).join(' ').trim()
            return {
              id: agent?.id,
              name: fullName || agent?.name || agent?.lastname || '',
            }
          })
        })
        .catch(error => {
          console.log(error)
          optionsAgentInsurance.value = []
        })
    }

    onMounted(() => {
      loadTypeVisit()
      loadReferencesList()
      loadNameInsurance()
      loadInsuranceAgents()
      
      // Forzar que todos los campos se marquen como touched para mostrar errores
      setTimeout(() => {
        if (refFormObserver.value && refFormObserver.value.fields) {
          Object.keys(refFormObserver.value.fields).forEach(fieldName => {
            if (refFormObserver.value.fields[fieldName]) {
              refFormObserver.value.fields[fieldName].touched = true
            }
          })
        }
      }, 100)
    })

    const loadReferencesList = () => {
      axiosP.referencePagination(10, 1).then(({ data }) => {
        optionsReferenceList.value = data.data
      })
    }

    const handleDataMemberchip = dato => {
      membershipData.value = dato
    }

    const memberIdLength = computed(() =>
      (stateData.value.member_id || '').length
    )

    const memberIdRemaining = computed(
      () => memberIdMaxLength - memberIdLength.value
    )

    const forceValidation = async () => {
      // Forzar que todos los campos se marquen como touched
      const fields = refFormObserver.value.fields
      Object.keys(fields).forEach(fieldName => {
        if (fields[fieldName]) {
          fields[fieldName].touched = true
          // Validar cada campo individualmente para mostrar errores
          if (fields[fieldName].validate) {
            fields[fieldName].validate()
          }
        }
      })
      // Forzar validación de todos los campos antes del envío
      await refFormObserver.value.validate()
    }

    const onSubmit = async () => {
      // Primero, forzar que todos los campos se marquen como touched
      const fields = refFormObserver.value.fields
      if (fields) {
        Object.keys(fields).forEach(fieldName => {
          if (fields[fieldName]) {
            // Marcar como touched inmediatamente
            fields[fieldName].touched = true
            // Validar cada campo individualmente para mostrar errores
            if (fields[fieldName].validate) {
              fields[fieldName].validate()
            }
          }
        })
      }
      
      // Esperar un momento para que las validaciones se completen y se actualice el DOM
      await new Promise(resolve => setTimeout(resolve, 150))
      
      // Forzar validación de todos los campos
      const isValid = await refFormObserver.value.validate()
      
      // Validación manual adicional para garantizar que todos los campos requeridos estén llenos
      const errors = []
      
      // Validar campos básicos requeridos
      if (!stateData.value.name || stateData.value.name.trim() === '') {
        errors.push('Name')
      }
      if (!stateData.value.lastname || stateData.value.lastname.trim() === '') {
        errors.push('Last Name')
      }
      // Validar teléfono (debe tener al menos 10 dígitos después de limpiar el formato)
      const phoneCleaned = stateData.value.phone ? stateData.value.phone.replace(/[() -]/g, '') : ''
      if (!stateData.value.phone || stateData.value.phone.trim() === '' || phoneCleaned.length < 10) {
        errors.push('Phone')
      }
      if (!stateData.value.gender) {
        errors.push('Gender')
      }
      if (!stateData.value.type_visits_id) {
        errors.push('Type visit')
      }
      if (!stateData.value.agent_insurance_id) {
        errors.push('Insurance Agent')
      }
      if (!stateData.value.nameInsuranceId) {
        errors.push('Name insurance')
      }
      if (!stateData.value.member_id || stateData.value.member_id.trim() === '') {
        errors.push('Member ID')
      }
      if (!stateData.value.reason_visit) {
        errors.push('Visit Reason')
      }
      if (!stateData.value.address || stateData.value.address.trim() === '') {
        errors.push('Address')
      }
      if (!stateData.value.cities_id) {
        errors.push('City')
      }
      if (stateData.value.zip_codes_id === null || stateData.value.zip_codes_id === undefined || stateData.value.zip_codes_id === '') {
        errors.push('Zip Code')
      }
      if (!stateData.value.date_birth || stateData.value.date_birth.trim() === '') {
        errors.push('Date of Birth')
      }
      
      // Validar campos numéricos requeridos (deben ser números válidos >= 0)
      const copayValue = parseFloat(stateData.value.copay)
      if (stateData.value.copay === '' || stateData.value.copay === null || stateData.value.copay === undefined || isNaN(copayValue) || copayValue < 0) {
        errors.push('Copay')
      }
      const coInsuranceValue = parseFloat(stateData.value.co_insurance)
      if (stateData.value.co_insurance === '' || stateData.value.co_insurance === null || stateData.value.co_insurance === undefined || isNaN(coInsuranceValue) || coInsuranceValue < 0) {
        errors.push('Co Insurance')
      }
      const deducibleValue = parseFloat(stateData.value.deducible)
      if (stateData.value.deducible === '' || stateData.value.deducible === null || stateData.value.deducible === undefined || isNaN(deducibleValue) || deducibleValue < 0) {
        errors.push('Deducible')
      }
      const outPocketValue = parseFloat(stateData.value.out_pocket)
      if (stateData.value.out_pocket === '' || stateData.value.out_pocket === null || stateData.value.out_pocket === undefined || isNaN(outPocketValue) || outPocketValue < 0) {
        errors.push('Out Pocket')
      }
      
      // Validar campos condicionales
      if (stateData.value.reason_visit === 'Other' && (!stateData.value.reason_visit_other || stateData.value.reason_visit_other.trim() === '')) {
        errors.push('Other Reason')
      }
      
      // Validar Reference si es requerido (type_visits_id === 2, 3, 13, 14)
      if ((stateData.value.type_visits_id === 2 || 
           stateData.value.type_visits_id === 3 || 
           stateData.value.type_visits_id === 13 || 
           stateData.value.type_visits_id === 14) && 
          !stateData.value.user_references_id) {
        errors.push('Reference')
      }
      
      // Si hay errores de validación o campos faltantes, no permitir el envío
      if (!isValid || errors.length > 0) {
        // Marcar todos los campos como touched nuevamente para asegurar que se muestren los errores
        if (fields) {
          Object.keys(fields).forEach(fieldName => {
            if (fields[fieldName]) {
              fields[fieldName].touched = true
            }
          })
        }
        
        // Mostrar mensaje de error
        const errorMessage = errors.length > 0 
          ? `${$t('Please fill in all required fields')}: ${errors.join(', ')}`
          : $t('Please fill in all required fields.')
        toast.value.error(errorMessage)
        return // Detiene el proceso si hay errores - NO SE GUARDA
      }

      loading.value = true
      const phone = stateData.value.phone.replace(/[() -]/g, '')
      
      // Determine the final reason_visit value
      let finalReasonVisit = stateData.value.reason_visit
      if (stateData.value.reason_visit === 'Other' && stateData.value.reason_visit_other) {
        finalReasonVisit = stateData.value.reason_visit_other
      }
      
      // Create a copy of stateData without reason_visit_other
      const { reason_visit_other, ...stateDataWithoutOther } = stateData.value
      
      const datos = {
        ...stateDataWithoutOther,
        phone,
        reason_visit: finalReasonVisit,
        ...membershipData.value,
        type_membership: 'NP',
      }
      await axiosP
        .patientCreate(datos)
        .then(res => {
          const { type, message } = notificationCheck(res)
          const patientId =
            res?.data?.id ?? res?.id ?? res?.registro?.id ?? res?.data?.user?.id ?? null
          emit('createPatient', { type, message, patientId, patient: res?.data ?? res })
          loading.value = false
          resetuserData()
        })
        .catch(error => {
          loading.value = false
          const { type, message } = notificationCheck(error.response)
          emit('createPatient', { type, message })
        })
    }

    const { refFormObserver, getValidationState: originalGetValidationState, resetForm } =
      formValidation(resetuserData)

    // Wrapper para forzar mostrar errores
    const getValidationState = (validationContext) => {
      // Si hay errores o el campo está touched y no es válido, mostrar estado de error
      if (validationContext.errors && validationContext.errors.length > 0) {
        return false
      }
      
      // Si el campo está touched y no es válido, mostrar estado de error
      if (validationContext.touched && !validationContext.valid) {
        return false
      }
      
      const result = originalGetValidationState(validationContext)
      return result
    }

    return {
      loading,
      stateData,
      formatPhone,
      toast,
      checkCodeQr,
      inputScan,
      scanPromotion,
      onSubmit,
      forceValidation,
      isAddNewConsultSidebarActive,
      createConsult,
      optionsTypeVisit,
      optionsNameInsurance,
      optionsAgentInsurance,
      searchTypeVisit,
      optionsReferenceList,
      cityName,
      cityOptions,
      searchCitiesFromInput,
      onCitySelect,
      selectCityOption,
      refFormObserver,
      getValidationState,
      resetForm,
      config: {
        dateFormat: 'm-d-Y',
        allowInput: true,
        static: true,
      },
      cancelCupon,
      showConditionalContent,
      showConditionalContentRef,
      showOtherReasonInput,
      handleDataMemberchip,
      membershipData,
      memberIdMaxLength,
      memberIdLength,
      memberIdRemaining,
    }
  },
}
</script>

<style lang="scss" scoped>
.button-custom {
  padding: 5px !important;
}

.city-autocomplete-wrap {
  position: relative;
}

.city-options-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  max-height: 200px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e1e5e9;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 2px;
}

.city-option-item {
  padding: 8px 12px;
  cursor: pointer;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.15s ease;
}

.city-option-item:hover {
  background-color: #f8f9fa;
}

.city-option-item:last-child {
  border-bottom: none;
}

.flatpickr-wrapper {
  width: 100% !important;
}

.is-invalid {
  border-color: red;
}

.custom-treeselect {
  :deep(.vue-treeselect__control) {
    border-radius: 8px;
    border: 2px solid #e1e5e9;
    transition: all 0.3s ease;
    font-size: 14px;
    padding: 8px 12px;
    background-color: #fff;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    min-height: 48px;
    
    &:hover {
      border-color: #007bff;
    }
  }
  
  :deep(.vue-treeselect__control--focused) {
    border-color: #007bff;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    outline: none;
  }
  
  :deep(.vue-treeselect__placeholder) {
    color: #6c757d;
    font-style: italic;
  }
  
  :deep(.vue-treeselect__menu) {
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border: 1px solid #e1e5e9;
    max-height: 200px;
    overflow-y: auto;
  }
  
  :deep(.vue-treeselect__option) {
    padding: 10px 16px;
    font-size: 14px;
    line-height: 1.4;
    transition: background-color 0.2s ease;
  }
  
  :deep(.vue-treeselect__option:hover) {
    background-color: #f8f9fa;
  }
  
  :deep(.vue-treeselect__option--selected) {
    background-color: #007bff;
    color: white;
  }
  
  :deep(.vue-treeselect__input) {
    font-size: 14px;
    padding: 4px 0;
  }
  
  :deep(.vue-treeselect__single-value) {
    font-size: 14px;
    color: #495057;
  }
}

// Estilo para el treeselect cuando está en estado de error
.custom-treeselect.is-invalid {
  :deep(.vue-treeselect__control) {
    border-color: #dc3545 !important;
    border-width: 2px !important;
  }
  
  :deep(.vue-treeselect__control--focused) {
    border-color: #dc3545 !important;
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
  }
}
</style>

